/* ================================================================
   ZZ MARTS — MOBILE BOTTOM BAR
   ----------------------------------------------------------------
   Two independent jobs, two independent IIFEs, on purpose:

     PART 1  measures the fixed bottom furniture and publishes the
             real heights as CSS variables. Runs on EVERY page.
     PART 2  wires the bar's buttons. Returns early when the bar is
             absent, which is why it must not be merged with Part 1.

   ================================================================
   PART 1 — WHY THE HEIGHTS ARE MEASURED, NOT WRITTEN DOWN
   ================================================================
   Three elements share the bottom of a product page on a phone:

       .zzbar      mobile bottom bar     z-index 950
       .zzsticky   sticky add to cart    z-index 880
       .zzwa       WhatsApp float        z-index 900

   Each one has to clear the one below it, so two of them need to
   know a height they do not own. That was previously solved by
   writing the numbers down in three places: --zz-stickybar-h was
   54px, .zzsticky sat at bottom:58px, and .zzwa added them up by
   hand. Measured live, BOTH bars render 59px. Every offset was
   wrong, and the WhatsApp button covered the Add to cart button —
   the exact collision the bar was reinstated to avoid.

   Hard-coded heights cannot survive: the safe-area inset differs per
   device, the bar grows if a label wraps, and the sticky bar grows
   with the font size. So nothing is written down any more. The two
   bars are measured and published on <html>:

       --zz-mobilebar-h    real height of .zzbar,   0px on desktop
       --zz-stickybar-h    real height of .zzsticky

   getBoundingClientRect().height is used rather than the observer's
   contentRect BECAUSE it includes padding and border. .zzbar carries
   padding-bottom: env(safe-area-inset-bottom), so the measured value
   already contains the notch allowance and no CSS anywhere needs to
   mention env() again.

   The desktop case falls out for free: .zzbar is display:none above
   749px, so it measures 0px and every calc() collapses to the plain
   desktop offset. One formula, both breakpoints.

   Transform does not affect the measurement, so the values stay
   correct while either bar is slid out of view.
   ================================================================ */
(function () {
  'use strict';

  var root = document.documentElement;

  var TRACKED = [
    { sel: '.zzbar',    prop: '--zz-mobilebar-h' },
    { sel: '.zzsticky', prop: '--zz-stickybar-h' }
  ];

  function publish(entry) {
    var el = document.querySelector(entry.sel);
    if (!el) { root.style.removeProperty(entry.prop); return; }
    var h = Math.round(el.getBoundingClientRect().height);
    root.style.setProperty(entry.prop, h + 'px');
  }

  function publishAll() { TRACKED.forEach(publish); }

  function start() {
    publishAll();

    if (typeof ResizeObserver === 'function') {
      TRACKED.forEach(function (entry) {
        var el = document.querySelector(entry.sel);
        if (!el) return;
        new ResizeObserver(function () { publish(entry); }).observe(el);
      });
    }

    /* ResizeObserver does not fire when an element flips to
       display:none at a breakpoint, so the crossing is caught here.
       Without this the sticky bar would keep a 59px offset on a
       desktop window that was resized down and back up. */
    var mq = window.matchMedia('(max-width: 749px)');
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', publishAll);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(publishAll);
    }

    window.addEventListener('orientationchange', function () {
      setTimeout(publishAll, 120);
    });

    /* Fonts land after first paint and change the bar height. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(publishAll).catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ================================================================
   PART 2 — BAR BUTTONS
   ----------------------------------------------------------------
   Wires three buttons and mirrors the cart count. Home is a plain
   anchor in the markup and never touches this file, so the bar is
   still navigable if the script fails to load.

   Each button reuses a surface the theme already has rather than
   building a second one:
     search      to #search-modal, the same dialog the header opens
     categories  to the details element inside header-drawer
     cart        to #cart-drawer, opened the way zz-cart.js does

   THE CATEGORIES BUTTON CLICKS THE SUMMARY, it does not set
   details.open = true. Setting the property fires no click event,
   and Horizon hangs its focus trap and scroll lock off the summary's
   click handler, so the panel would open with the page still
   scrolling behind it and focus left down on the bar. Clicking is
   what the real hamburger does, so it is what this does too.
   ================================================================ */
(function () {
  'use strict';

  var bar = document.querySelector('[data-zzbar]');
  if (!bar) return;

  function q(sel) { return document.querySelector(sel); }

  /* ---------------- actions ---------------- */

  var actions = {
    search: function () {
      var modal = q('#search-modal');
      if (modal && typeof modal.showDialog === 'function') { modal.showDialog(); return; }
      if (modal && typeof modal.showModal === 'function') { modal.showModal(); return; }
      window.location.href = '/search';
    },

    menu: function () {
      var summary = q('header-drawer details > summary');
      if (summary) { summary.click(); return; }
      window.location.href = '/collections/all';
    },

    cart: function () {
      var drawer = q('#cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      window.location.href = '/cart';
    }
  };

  bar.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-zzbar-action]');
    if (!btn) return;
    var name = btn.getAttribute('data-zzbar-action');
    if (!actions[name]) return;
    event.preventDefault();
    actions[name]();
  });

  /* ---------------- cart count ----------------
     Mirrors the header bubble rather than tracking the cart itself.
     .cart-bubble is ALWAYS in the DOM and merely carries
     .visually-hidden when the cart is empty, so its presence proves
     nothing and only its text is usable. */

  var badge = bar.querySelector('[data-zzbar-count]');

  function syncCount() {
    if (!badge) return;
    var bubble = q('.cart-bubble');
    var n = bubble ? parseInt((bubble.textContent || '').replace(/\D/g, ''), 10) : 0;
    if (!n || isNaN(n)) {
      badge.hidden = true;
      badge.textContent = '';
    } else {
      badge.hidden = false;
      badge.textContent = n > 99 ? '99+' : String(n);
    }
  }

  function watchCount() {
    var bubble = q('.cart-bubble');
    if (!bubble) return;
    new MutationObserver(syncCount).observe(bubble, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  /* ---------------- open state ----------------
     Lights the Categories tab while its drawer is open, so the bar
     reflects what is on screen instead of going stale. */

  function watchMenuState() {
    var details = q('header-drawer details');
    var btn = bar.querySelector('[data-zzbar-action="menu"]');
    if (!details || !btn) return;
    var apply = function () { btn.classList.toggle('is-open', details.open); };
    new MutationObserver(apply).observe(details, {
      attributes: true,
      attributeFilter: ['open']
    });
    apply();
  }

  function init() {
    syncCount();
    watchCount();
    watchMenuState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
